import { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useI18n } from "@/i18n/I18nContext";
import { useSite } from "@/context/SiteContext";
import { YouTubeIcon, TikTokIcon, InstagramIcon } from "@/components/icons";
import { LangSwitcher } from "@/components/Navbar";

export const Footer = () => {
  const { t, lang } = useI18n();
  const pressLabel = ({ it: "Dicono di noi", en: "In the news", es: "Hablan de nosotros", pt: "Falam sobre nós" })[lang] || "Ananawi sulla stampa";
  const { content, setConsent } = useSite();
  const s = content?.socials ?? {};
  const socials = [["youtube", YouTubeIcon], ["tiktok", TikTokIcon], ["instagram", InstagramIcon]];
  return (
    <footer className="relative bg-ink text-white pt-20 pb-10 overflow-hidden" data-testid="footer">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <img src="/brand/logo-360.webp" width="360" height="239" loading="lazy" alt="Ananawi" className="h-24 w-auto" />
          <p className="mt-4 text-white/80 font-bold max-w-sm">{t.footer.tagline}</p>
          <div className="mt-6 flex gap-3">
            {socials.map(([k, Icon]) => (
              <a key={k} href={s[k]} target="_blank" rel="me noopener noreferrer" aria-label={k} data-social-platform={k} className="grid place-items-center w-12 h-12 rounded-full bg-white/10 hover:bg-sun hover:text-ink transition-colors duration-200" data-testid={`footer-social-${k}`}>
                <Icon className="w-5 h-5" />
              </a>
            ))}
          </div>
        </div>
        <div className="md:col-span-7 grid grid-cols-1 sm:grid-cols-3 gap-8 text-sm font-bold">
          <nav className="flex flex-col items-start gap-3" aria-label="Legal" data-testid="footer-col-1">
            <Link to="/privacy" className="text-white/85 hover:text-sun transition-colors" data-testid="footer-privacy-link">{t.footer.privacy}</Link>
            <Link to="/termini" className="text-white/85 hover:text-sun transition-colors" data-testid="footer-terms-link">{t.footer.terms}</Link>
            <Link to="/stampa" className="text-white/85 hover:text-sun transition-colors">{pressLabel}</Link>
            <Link to="/genitori" className="text-white/85 hover:text-sun transition-colors" data-testid="footer-parents-link">{t.nav.parents}</Link>
          </nav>
          <div className="flex flex-col items-start gap-3" data-testid="footer-col-2">
            <Link to="/cookie" className="text-white/85 hover:text-sun transition-colors" data-testid="footer-cookie-link">{t.footer.cookie}</Link>
            <button onClick={() => setConsent("")} className="text-left text-white/85 hover:text-sun transition-colors" data-testid="footer-cookie-prefs">{t.footer.prefs}</button>
          </div>
          <div className="flex sm:justify-end items-start" data-testid="footer-col-3">
            <LangSwitcher testid="footer-lang-switcher" />
          </div>
        </div>
      </div>
      <div className="mx-auto max-w-7xl px-5 sm:px-8 mt-14 pt-6 border-t border-white/15 text-xs text-white/70 font-bold flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1.5">
        <span>© {new Date().getFullYear()} Ananawi. {t.footer.rights}</span>
        <span data-testid="footer-official">{t.footer.official}</span>
      </div>
      <p className="pointer-events-none select-none font-display font-bold text-[22vw] leading-none text-white/[0.05] absolute -bottom-[4vw] left-0 right-0 text-center">ananawi</p>
    </footer>
  );
};

export const CookieBanner = () => {
  const { t } = useI18n();
  const { consent, setConsent } = useSite();
  const [space, setSpace] = useState(0);
  const bannerRef = useCallback((el) => {
    if (!el) return;
    const ro = new ResizeObserver(() => setSpace(el.offsetHeight));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <>
    {/* Mobile only: page space under the fixed banner so it never hides content (incl. safe area). */}
    {!consent && <div aria-hidden className="sm:hidden" style={{ height: `calc(${space + 24}px + env(safe-area-inset-bottom))` }} data-testid="cookie-banner-spacer" />}
    <AnimatePresence>
      {!consent && (
        <motion.div
          ref={bannerRef}
          initial={{ y: 120, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 120, opacity: 0 }}
          transition={{ type: "spring", stiffness: 200, damping: 22, delay: 1.2 }}
          className="fixed z-[65] left-3 right-3 bottom-[calc(0.75rem+env(safe-area-inset-bottom))] sm:left-6 sm:right-auto sm:bottom-6 sm:max-w-md rounded-[26px] sticker bg-white p-5"
          data-testid="cookie-banner"
        >
          <div className="flex gap-4">
            <img src="/brand/f_tongue.webp" alt="" className="w-14 h-14 object-contain shrink-0" />
            <div>
              <p className="font-display font-bold text-lg">{t.cookie.title}</p>
              <p className="mt-1 text-sm text-ink/80 leading-relaxed">{t.cookie.text}</p>
              <Link to="/cookie" className="mt-1 inline-block text-sm font-extrabold underline" data-testid="cookie-banner-more">{t.cookie.more}</Link>
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <button onClick={() => setConsent("necessary")} className="flex-1 rounded-full border-[2.5px] border-ink px-4 py-2.5 font-extrabold text-sm hover:bg-cream transition-colors" data-testid="cookie-reject-btn">{t.cookie.reject}</button>
            <button onClick={() => setConsent("all")} className="flex-1 btn-pop btn-pop-sm sticker-sm rounded-full bg-leaf text-white px-4 py-2.5 font-extrabold text-sm" data-testid="cookie-accept-btn">{t.cookie.accept}</button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
    </>
  );
};
